import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "@/assets/logo-sm.png";
import { whatsappUrl } from "@/lib/brand";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { key: "home", href: "#inicio" },
    { key: "collections", href: "#colecoes" },
    { key: "about", href: "#sobre" },
    { key: "differentials", href: "#diferenciais" },
    { key: "contact", href: "#contato" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const talkMessage = t("common.defaultWhatsappMessage");

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-[#F9F9F6]/90 backdrop-blur-md border-b border-[#E5E5E0]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#inicio" data-testid="navbar-logo" className="flex items-start">
          <img src={logo} alt={t("nav.logoAlt")} className="h-7 md:h-8 w-auto" />
          <sup
            aria-label={t("nav.trademarkAria")}
            className="ml-0.5 mt-0.5 text-[9px] md:text-[10px] font-body font-medium text-[#1A1A1A]/50 leading-none select-none"
          >
            ®
          </sup>
        </a>

        <ul className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="font-body text-sm font-medium text-[#1A1A1A]/80 hover:text-[#2A3563] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[#C86A3D] hover:after:w-full after:transition-all"
              >
                {t(`nav.${l.key}`)}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher variant="header" />
          <a
            href={whatsappUrl(talkMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-[#2A3563] text-white font-body font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-[#1f2749] transition-colors"
          >
            <MessageCircle size={16} /> {t("nav.whatsapp")}
          </a>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher variant="header" />
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#2A3563]"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("nav.menuAria")}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#F9F9F6] border-b border-[#E5E5E0]"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="font-body text-base text-[#1A1A1A]"
                  >
                    {t(`nav.${l.key}`)}
                  </a>
                </li>
              ))}
              <a
                href={whatsappUrl(talkMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-body font-semibold px-5 py-3 rounded-sm"
              >
                <MessageCircle size={18} /> {t("nav.whatsappTalk")}
              </a>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
